import React from "react";
import {
  DatePicker,
  DayOfWeek,
  Dropdown,
  IDropdownOption,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  Toggle,
} from "office-ui-fabric-react";
import { getManualReadingStatusCount } from "../../services/dashboard/DashboardService";
import { IDataCenter, IManualReadingStatusCount } from "../../vm";
import { childrenGap } from "../common/Constants";
import { ToastContext } from "../common/ToastProvider";
import moment from "moment";
import { ResponsiveBar } from "@nivo/bar";
import { getPercentageValue } from "../../services/UtilService";
import ManualReadingDetailsModal from "./ManualReadingDetailsModal";
import { Loading } from "../../services/Loading";

export interface ManualReadingDashboardProps {
  datacenters: IDataCenter[];
}
const wrapperStyles = {
  // width: "100%",
  // maxWidth: 980,
  margin: "0 auto",
};

const ManualReadingDashboard: React.FC<ManualReadingDashboardProps> = ({
  datacenters,
}) => {
  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  // state
  const [state, setState] = React.useState({
    isLoading: false,
    manualReadingStatus: [],
    searchObj: {
      startDate: new Date(),
      endDate: new Date(),
      datacenterId: datacenters.length > 0 ? datacenters[0]?._id : "",
    },
    date: new Date(),
    datacenterOptions: [],
    selectedDatacenter: datacenters.length > 0 ? datacenters[0]?._id : "",
    graphToggle: false,
    manageModal: {
      isOpen: false,
      unit: "",
    },
  } as {
    isLoading: boolean;
    manualReadingStatus: IManualReadingStatusCount[];
    searchObj: {
      startDate: Date | string;
      endDate: Date | string;
      datacenterId: string;
    };
    date: Date;
    datacenterOptions: IDropdownOption[];
    selectedDatacenter: string;
    graphToggle: boolean;
    manageModal: {
      isOpen: boolean;
      unit: string;
    };
  });

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      await getManualReadingStatus();
    };
    asyncFunc();
  }, []);

  const getManualReadingStatus = async (paramState?: any) => {
    let localState = { ...state };
    if (paramState) {
      localState = { ...paramState };
    }
    setState({ ...localState, isLoading: true });
    let searchObj = { ...localState.searchObj };
    searchObj.startDate = moment(localState.date).format("YYYY-MM-DD");
    searchObj.endDate = moment(localState.date).format("YYYY-MM-DD");

    let result = await getManualReadingStatusCount(searchObj);
    if (result?.success) {
      let datacenterOptions: IDropdownOption[] = [];
      if (datacenters.length > 0) {
        datacenters.forEach((d) => {
          datacenterOptions.push({
            key: d._id as string,
            text: d.name,
          });
        });
      }

      setState({
        ...localState,
        manualReadingStatus: result.data,
        // searchObj,
        datacenterOptions,
        // selectedDatacenter:
        //   datacenters.length > 0 ? (datacenters[0]?._id as string) : "",
      });
    } else {
      setState({ ...localState, isLoading: false });
      showToast(
        result?.message || "Error while getting manual readings",
        "error"
      );
    }
  };

  const handleModalOpenClose = (isOpen: boolean, unit?: string) => {
    setState({
      ...state,
      manageModal: {
        isOpen,
        unit: unit ? unit : "",
      },
    });
  };

  return (
    <React.Fragment>
      {/* {state.isLoading && <Loading />} */}
      <div style={wrapperStyles}>
        <div className="ms-Grid p-0">
          <div className="ms-Grid-row ms-motion-scaleDownIn status-count-card overflow ml-0 mr-0">
            {state.isLoading && (
              <div className="custom-card spinner-center">
                <Spinner
                  size={SpinnerSize.large}
                // label="Loading"
                // labelPosition="bottom"
                />
              </div>
            )}
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12">
              <Stack
                horizontal
                horizontalAlign="space-between"
                wrap
                tokens={childrenGap}
              >
                <Stack.Item>
                  <Text className="dashboard-card-title">Manual Readings</Text>
                </Stack.Item>
                <Stack.Item>
                  <Stack horizontal wrap tokens={childrenGap}>
                    <Stack.Item>
                      <Dropdown
                        selectedKey={state.selectedDatacenter}
                        styles={{ dropdown: { width: 200 } }}
                        options={state.datacenterOptions}
                        onChange={async (e, v: IDropdownOption | undefined) => {
                          if (v) {
                            let obj = {
                              ...state,
                              selectedDatacenter: v.key as string,
                              searchObj: {
                                ...state.searchObj,
                                datacenterId: v.key as string,
                              },
                            };
                            // setState();
                            await getManualReadingStatus(obj as any);
                          }
                        }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <DatePicker
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        value={new Date(state.date)}
                        showGoToToday={false}
                        onSelectDate={async (date: Date | null | undefined) => {
                          let obj = {
                            ...state,
                            date: date as Date,
                          };
                          await getManualReadingStatus(obj as any);
                        }}
                        styles={{ root: { width: 200 } }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Toggle
                        className="pointer"
                        onText="Grouped Chart"
                        offText="Grouped Chart"
                        onChange={(e, v) => {
                          setState({
                            ...state,
                            graphToggle: v as boolean,
                          });
                        }}
                      />
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
              <hr />
              <div className="ms-Grid-row">
                <div className="ms-Grid-col pt-0 ms-sm12 ms-md12 ms-lg12">
                  <div
                    className={`bar-container ${state.manualReadingStatus.length === 0 ? "no-data" : ""
                      }`}
                  >
                    {state.manualReadingStatus.length > 0 ? (
                      <ResponsiveBar
                        data={state.manualReadingStatus.map((d) => {
                          return {
                            id: d.type,
                            label: d.label,
                            Approved: getPercentageValue(
                              d.approved,
                              d.totalCount
                            ),
                            ApprovedColor: "#008000",
                            Submitted: getPercentageValue(
                              d.submitted,
                              d.totalCount
                            ),
                            SubmittedColor: "#135c8a",
                            Rejected: getPercentageValue(
                              d.rejected,
                              d.totalCount
                            ),
                            RejectedColor: "#d62828",
                            "Not Submitted": getPercentageValue(
                              d.totalCount -
                              d.approved -
                              d.submitted -
                              d.rejected,
                              d.totalCount
                            ),
                            "Not SubmittedColor": "#daa520",
                            // "Not In Threshold": getPercentageValue(
                            //   d.notInThreshold,
                            //   d.totalCount
                            // ),
                            // "Not In ThresholdColor": "#FF7F50",
                          };
                        })}
                        keys={[
                          "Not Submitted",
                          "Submitted",
                          "Approved",
                          "Rejected",
                          // "Not In Threshold",
                        ]}
                        onClick={(data) =>
                          handleModalOpenClose(true, data.data.id as string)
                        }
                        groupMode={state.graphToggle ? "grouped" : "stacked"}
                        indexBy="label"
                        margin={{ top: 50, right: 30, bottom: 50, left: 60 }}
                        padding={state.graphToggle === true ? 0.1 : 0.3}
                        colors={({ id, data }) => data[`${id}Color`]}
                        borderColor={{
                          from: "color",
                          modifiers: [["darker", 1.6]],
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 10,
                          tickPadding: 10,
                          tickRotation: 0,
                          //   legend: "country",
                          //   legendPosition: "middle",
                          //   legendOffset: 32,
                        }}
                        axisLeft={{
                          tickSize: 10,
                          tickPadding: 15,
                          tickRotation: 0,
                          //   legend: "food",
                          //   legendPosition: "middle",
                          //   legendOffset: -40,
                          format: (v) => `${v}%`,
                          // tickValues: [
                          //   "0%",
                          //   "10%",
                          //   "20%",
                          //   "30%",
                          //   "40%",
                          //   "50%",
                          //   "60%",
                          //   "70%",
                          //   "80%",
                          //   "90%",
                          //   "100%",
                          // ],
                          // renderTick: (tick) => tick + "%",
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        // labelTextColor={{
                        //   from: "color",
                        //   modifiers: [["darker", 1.6]],
                        // }}
                        labelTextColor="#fff"
                        legends={[
                          {
                            dataFrom: "keys",
                            anchor: "top",
                            direction: "row",
                            justify: false,
                            translateX: 20,
                            translateY: -50,
                            itemsSpacing: 5,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: "left-to-right",
                            itemOpacity: 0.85,
                            symbolSize: 20,
                            effects: [
                              {
                                on: "hover",
                                style: {
                                  itemOpacity: 1,
                                },
                              },
                            ],
                          },
                        ]}
                        animate={true}
                        motionStiffness={90}
                        motionDamping={15}
                      />
                    ) : (
                      <>
                        <br />
                        <Text>No Readings Found</Text>
                        <br />
                        <br />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {state.manageModal.isOpen && (
        <ManualReadingDetailsModal
          startDate={state.date}
          endDate={state.date}
          datacenterId={state.searchObj.datacenterId}
          unit={state.manageModal.unit}
          handleModalClose={(isOpen: boolean) => handleModalOpenClose(isOpen)}
        />
      )}
    </React.Fragment>
  );
};

export default ManualReadingDashboard;
